/**
 * Custom hook encapsulating all immutable update helpers for the ArticleDocument draft.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { blogRunsService } from '../../services/blogRunsService';
import { blogCandidatesService } from '../../services/blogCandidatesService';
import type {
  ArticleDocument,
  ArticleSection,
  FaqItem,
  InternalLinkCandidate,
  ReferenceSource,
  SeoMetadata,
  Thesis,
} from './types';

interface UseDocumentDraftOptions {
  originalDoc: ArticleDocument | null;
  runId: string | undefined;
  candidate: { candidate_id: string; candidate_name: string } | null | undefined;
  refreshDocument: () => Promise<void>;
  refreshArticles: () => Promise<void>;
}

export function useDocumentDraft({ originalDoc, runId, candidate, refreshDocument, refreshArticles }: UseDocumentDraftOptions) {
  const [draft, setDraft] = useState<ArticleDocument | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const scrolledRef = useRef(false);

  useEffect(() => {
    if (originalDoc) {
      setDraft(JSON.parse(JSON.stringify(originalDoc)));
      setSavedAt(null);
      setSaveError(null);
      scrolledRef.current = false;
    }
  }, [originalDoc]);

  // ---- Thesis ----

  const updateThesis = useCallback((field: keyof Thesis, value: string) => {
    setDraft((prev) => {
      if (!prev) return prev;
      return { ...prev, thesis: { ...prev.thesis, [field]: value } };
    });
  }, []);

  // ---- SEO ----

  const updateSeo = useCallback((field: keyof SeoMetadata, value: string) => {
    setDraft((prev) => {
      if (!prev) return prev;
      const article = prev.article || {};
      const seo = article.seo || { meta_title: '', meta_description: '', slug: '' };
      return { ...prev, article: { ...article, seo: { ...seo, [field]: value } } };
    });
  }, []);

  // ---- Article fields ----

  const updateArticleField = useCallback((field: string, value: unknown) => {
    setDraft((prev) => {
      if (!prev) return prev;
      const article = prev.article || {};
      return { ...prev, article: { ...article, [field]: value } };
    });
  }, []);

  // ---- Sections ----

  const updateSection = useCallback((index: number, field: keyof ArticleSection, value: unknown) => {
    setDraft((prev) => {
      if (!prev) return prev;
      const sections = [...(prev.article?.sections || [])];
      sections[index] = { ...sections[index], [field]: value };
      return { ...prev, article: { ...prev.article, sections } };
    });
  }, []);

  const addSection = useCallback(() => {
    setDraft((prev) => {
      if (!prev) return prev;
      const sections = [...(prev.article?.sections || [])];
      sections.push({
        id: `sec_${Date.now()}`,
        heading: '',
        heading_level: 'H2',
        purpose: '',
        key_points: [],
        keywords_to_include: [],
        internal_links: [],
        research_needs: [],
        body: null,
      });
      return { ...prev, article: { ...prev.article, sections } };
    });
  }, []);

  const removeSection = useCallback((index: number) => {
    setDraft((prev) => {
      if (!prev) return prev;
      const sections = (prev.article?.sections || []).filter((_, i) => i !== index);
      return { ...prev, article: { ...prev.article, sections } };
    });
  }, []);

  // ---- FAQ ----

  const updateFaq = useCallback((index: number, field: keyof FaqItem, value: string) => {
    setDraft((prev) => {
      if (!prev) return prev;
      const faq = [...(prev.article?.faq || [])];
      faq[index] = { ...faq[index], [field]: value };
      return { ...prev, article: { ...prev.article, faq } };
    });
  }, []);

  const addFaq = useCallback(() => {
    setDraft((prev) => {
      if (!prev) return prev;
      const faq = [...(prev.article?.faq || []), { question: '', answer: null }];
      return { ...prev, article: { ...prev.article, faq } };
    });
  }, []);

  const removeFaq = useCallback((index: number) => {
    setDraft((prev) => {
      if (!prev) return prev;
      const faq = (prev.article?.faq || []).filter((_, i) => i !== index);
      return { ...prev, article: { ...prev.article, faq } };
    });
  }, []);

  // ---- Internal Links ----

  const updateLink = useCallback((index: number, field: keyof InternalLinkCandidate, value: unknown) => {
    setDraft((prev) => {
      if (!prev) return prev;
      const cr = prev.content_research || {};
      const links = [...(cr.internal_link_candidates || [])];
      links[index] = { ...links[index], [field]: value };
      return { ...prev, content_research: { ...cr, internal_link_candidates: links } };
    });
  }, []);

  const addLink = useCallback(() => {
    setDraft((prev) => {
      if (!prev) return prev;
      const cr = prev.content_research || {};
      const links = [...(cr.internal_link_candidates || []), { target_page: '', url: '' }];
      return { ...prev, content_research: { ...cr, internal_link_candidates: links } };
    });
  }, []);

  const removeLink = useCallback((index: number) => {
    setDraft((prev) => {
      if (!prev) return prev;
      const cr = prev.content_research || {};
      const links = (cr.internal_link_candidates || []).filter((_, i) => i !== index);
      return { ...prev, content_research: { ...cr, internal_link_candidates: links } };
    });
  }, []);

  // ---- Reference Sources ----

  const updateRef = useCallback((index: number, field: keyof ReferenceSource, value: string) => {
    setDraft((prev) => {
      if (!prev) return prev;
      const cr = prev.content_research || {};
      const refs = [...(cr.reference_sources || [])];
      refs[index] = { ...refs[index], [field]: value };
      return { ...prev, content_research: { ...cr, reference_sources: refs } };
    });
  }, []);

  const addRef = useCallback(() => {
    setDraft((prev) => {
      if (!prev) return prev;
      const cr = prev.content_research || {};
      const refs = [...(cr.reference_sources || []), { source: '', url: '', use: '' }];
      return { ...prev, content_research: { ...cr, reference_sources: refs } };
    });
  }, []);

  const removeRef = useCallback((index: number) => {
    setDraft((prev) => {
      if (!prev) return prev;
      const cr = prev.content_research || {};
      const refs = (cr.reference_sources || []).filter((_, i) => i !== index);
      return { ...prev, content_research: { ...cr, reference_sources: refs } };
    });
  }, []);

  // ---- Save ----

  const handleSave = useCallback(async () => {
    if (!draft || !runId) return;
    setSaving(true);
    setSaveError(null);
    try {
      await blogRunsService.updateDocument(runId, draft);
      if (candidate && draft.thesis?.candidate_name && draft.thesis.candidate_name !== candidate.candidate_name) {
        try {
          await blogCandidatesService.update(candidate.candidate_id, {
            candidate_name: draft.thesis.candidate_name,
          });
        } catch {
          // Best effort
        }
      }
      setSavedAt(new Date().toLocaleTimeString());
      await refreshDocument();
      await refreshArticles();
    } catch (e: any) {
      setSaveError(e?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  }, [draft, runId, candidate, refreshDocument, refreshArticles]);

  return {
    draft,
    saving,
    saveError,
    savedAt,
    scrolledRef,
    updateThesis,
    updateSeo,
    updateArticleField,
    updateSection,
    addSection,
    removeSection,
    updateFaq,
    addFaq,
    removeFaq,
    updateLink,
    addLink,
    removeLink,
    updateRef,
    addRef,
    removeRef,
    handleSave,
  };
}
